import React, { useContext, useState } from 'react'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { Divider } from '@ui-kitten/components'
import { useDispatch, useSelector } from 'react-redux'
import { StackNavigationProp } from '@react-navigation/stack'
import { StyleService, useStyleSheet } from '@src/style/service'
import { BillingProduct, UiInlineAlert, UiStateNames } from '@src/types'
import { CheckoutContainer } from '@src/screens/Marketplace/components/checkout/CheckoutContainer'
import { ProductDescription } from '@src/screens/Marketplace/components/checkout/ProductDescription'
import { ProductPayment } from '@src/screens/Marketplace/components/checkout/ProductPayment'
import {
  usePromotionCode,
  usePurchaseOneTimePayment,
  useTaxEstimation,
} from '@src/screens/Marketplace/utils/hooks'
import { InlineAlert } from '@src/components'
import { userSelector, videoCallProductSelector } from '@src/selectors/app'
import { RootStackParamList } from '@src/navigation/types'
import { CustomEventTypes } from '@config'
import { NavigatorContext } from '@utils'
import { useShouldShowPurchaseFlow } from '@src/utils/hooks'
import { AddressType, AddressForm } from '@src/screens/Marketplace/components/checkout/AddressForm'
import { useSecureScreen } from '@src/hooks/useSecureScreen'
import { useNutritionistHubTracker } from '../hooks'

const SUGGESTED_CALL_DETAILS =
  'After purchasing your call, ' +
  'it will be automatically booked and shown in the Upcoming Calls section of the Nutritionist tab.'

export const VideoCallCheckoutScreen = () => {
  useSecureScreen()

  const styles = useStyleSheet(themedStyle)
  const { purchaseOneTimePayment } = usePurchaseOneTimePayment()
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>()
  const route = useRoute<RouteProp<RootStackParamList, 'VideoCallCheckout'>>()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const { suggestedCallFlow, meetingAt, coachId, product: productProp } = route.params || {}
  const dispatch = useDispatch()

  const shouldShowPurchaseFlow = useShouldShowPurchaseFlow()

  const track = useNutritionistHubTracker()

  const defaultProduct = useSelector(videoCallProductSelector)
  const product = productProp ?? defaultProduct

  const user = useSelector(userSelector)
  const address = user?.address

  const [showAddress] = useState(!address)

  const productKey = product?.key as BillingProduct
  const promotionCode = usePromotionCode(productKey)

  const { estimatedTax } = useTaxEstimation({
    productKey,
    promotionCode,
    zipCode: address?.zipCode,
    country: address?.country,
  })

  const onCheckoutPress = async () => {
    if (!product) {
      return
    }

    enableLoader()

    try {
      await purchaseOneTimePayment({ product, quantity: 1, promotionCode })

      track(CustomEventTypes.NutritionistHubSuccessfulCheckout, {
        product: {
          key: productKey,
          title: product.title,
        },
      })

      navigation.replace('DietitianAddonOrderConfirmed', {
        product,
        suggestedCallFlow,
        meetingAt,
        coachId,
      })

      dispatch({ type: 'nutritionistHub/fetchInsurancePolicy' })

      if (shouldShowPurchaseFlow) {
        dispatch({
          type: 'app/upsertUiState',
          payload: {
            name: UiStateNames.CheckoutCompleted,
            value: true,
          },
        })
        dispatch({ type: 'users/fetch' })
      }
    } finally {
      disableLoader()
    }
  }

  if (!product) {
    navigation.replace('Drawer', { screen: 'Dashboard', params: { screen: 'NutritionistHub' } })
    return null
  }

  const productTitle = product.title
  const productDescription =
    product.description ??
    `Discuss your health goals, and answer your nutrition-related questions through a video call with your licensed nutritionist.`
  const productDetails =
    product.details?.join('\n') ??
    'Your licensed nutritionist will message you within 1 business day to set up a video call consultation.'

  const navigateToAddressScreen = () => {
    navigation.navigate('ChangeAddress', { addressType: AddressType.Billing })
  }

  return (
    <CheckoutContainer checkoutButtonTitle="Purchase Now" onCheckout={onCheckoutPress}>
      <ProductDescription
        title={productTitle}
        description={productDescription}
        price={`$${product.price}`}
      />

      <ProductPayment
        product={product.key as BillingProduct}
        fullPrice={product.price}
        estimatedTax={estimatedTax}
        promotionCode={promotionCode}
      />

      <Divider appearance="large" />

      {showAddress && (
        <AddressForm
          addressType={AddressType.Billing}
          address={address}
          onEditAddressPress={navigateToAddressScreen}
        />
      )}

      {!meetingAt && (
        <InlineAlert
          category={UiInlineAlert.Info}
          message="HOW IT WORKS"
          secondaryMessage={suggestedCallFlow ? SUGGESTED_CALL_DETAILS : productDetails}
          style={styles.infoAlert}
        />
      )}
    </CheckoutContainer>
  )
}

const themedStyle = StyleService.create({
  infoAlert: {
    marginHorizontal: 16,
    marginVertical: 32,
  },
})
